import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Project, WorkRecord } from '@/types';
import Card from '@/components/ui/Card';
import { FolderKanban, Clock, ChevronRight } from 'lucide-react';

interface UserSectionsProps {
  projects: Project[];
  recentWorkRecord: WorkRecord | null;
  maxHeight?: number;
}

const UserSections: React.FC<UserSectionsProps> = ({ 
  projects, 
  recentWorkRecord, 
  maxHeight 
}) => {
  const navigate = useNavigate();

  const formatWorkRecordType = (record: WorkRecord) => {
    const typeMap = {
      'check_in': '签到',
      'check_out': '签退', 
      'work': '工作记录',
      'leave': '请假',
      'business_trip': '出差'
    };
    return typeMap[record.record_type] || record.record_type;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const containerStyle = maxHeight ? { height: `${maxHeight}px` } : {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={containerStyle}>
      
      {/* 我参与的项目 */}
      <Card className="flex flex-col">
        <Card.Header>
          <Card.Title className="flex items-center space-x-2">
            <FolderKanban className="h-5 w-5 text-blue-600" />
            <span>我参与的项目</span>
          </Card.Title>
        </Card.Header>
        <Card.Content className="flex-1 overflow-y-auto">
          {projects.length > 0 ? (
            <div className="space-y-3">
              {projects.slice(0, 5).map((project) => (
                <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{project.name}</h4>
                    <p className="text-sm text-gray-500">
                      状态: {project.status === 'in_progress' ? '进行中' : 
                             project.status === 'completed' ? '已完成' : '未开始'}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-blue-600">
                      {project.total_hours?.toFixed(1) || '0.0'} 小时
                    </div>
                    <div className="text-xs text-gray-500">累计工时</div>
                  </div>
                </div>
              ))}
              {projects.length > 5 && (
                <div className="text-center pt-2">
                  <button 
                    onClick={() => navigate('/projects')}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    查看全部 {projects.length} 个项目
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FolderKanban className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">暂无参与的项目</p>
              <p className="text-sm">请联系管理员将您添加到项目中</p>
            </div>
          )}
        </Card.Content>
      </Card>

      {/* 最近工作日志 */}
      <Card className="flex flex-col">
        <Card.Header>
          <Card.Title className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-green-600" />
              <span>最近工作日志</span>
            </div>
            <button
              onClick={() => navigate('/work-records')}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              查看全部
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </Card.Title>
        </Card.Header>
        <Card.Content className="flex-1 overflow-y-auto">
          {recentWorkRecord ? (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-green-800">
                  {formatWorkRecordType(recentWorkRecord)}
                </span>
                <span className="text-sm text-green-600">
                  {formatDate(recentWorkRecord.start_time)}
                </span>
              </div>
              {recentWorkRecord.project?.name && (
                <p className="text-sm text-green-700 mb-1">
                  项目: {recentWorkRecord.project.name}
                </p>
              )}
              {recentWorkRecord.description && (
                <p className="text-sm text-green-700 mb-2">
                  {recentWorkRecord.description}
                </p>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-green-600">
                  时长: {recentWorkRecord.duration_hours.toFixed(1)} 小时
                </span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  recentWorkRecord.status === 'approved' ? 'bg-green-100 text-green-800' :
                  recentWorkRecord.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {recentWorkRecord.status === 'approved' ? '已批准' :
                   recentWorkRecord.status === 'pending' ? '待审批' : '已拒绝'}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">暂无工作记录</p>
              <p className="text-sm">开始记录您的工作来查看最新日志</p>
              <button
                onClick={() => navigate('/work-records')}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800"
              >
                前往记录工作
              </button>
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default UserSections;